import { and, eq } from "drizzle-orm";
import {
  order,
  order_line,
  order_line_modifier,
  payment,
  type PowerSyncDrizzleDB,
} from "@comandero/powersync";
import { liveOrderLineFilters, livePaymentFilters } from "../live/filters";
import { nowISO } from "../utils/date-helpers";
import { generateUUID } from "../utils/uuid";
import {
  type CalcLine,
  changeDue,
  computeOrderTotals,
  isSplitSettled,
  lineTotal,
  lineUnitEffectivePrice,
  remainingDue,
} from "./calc";

export type OrderType = "dine_in" | "takeaway" | "delivery";
export type PaymentMethod = "cash" | "card" | "transfer" | "other";

export interface CreateOrderOpts {
  type?: OrderType;
  tableId?: string | null;
  waiterUserId?: string | null;
  employeeId?: string | null;
  registerSessionId?: string | null;
  guests?: number;
}

/** Crea una orden vacía (abierta). Devuelve el id. */
export async function createOrderMutation(
  db: PowerSyncDrizzleDB,
  organizationId: string,
  branchId: string,
  opts: CreateOrderOpts = {},
): Promise<string> {
  const id = generateUUID();
  const now = nowISO();
  await db.insert(order).values({
    id,
    organization_id: organizationId,
    branch_id: branchId,
    type: opts.type ?? "dine_in",
    table_id: opts.tableId ?? null,
    waiter_user_id: opts.waiterUserId ?? null,
    employee_id: opts.employeeId ?? null,
    register_session_id: opts.registerSessionId ?? null,
    status: "open",
    guests: opts.guests ?? 1,
    subtotal: 0,
    discount: 0,
    tax_total: 0,
    total: 0,
    opened_at: now,
    created_at: now,
    updated_at: now,
  });
  return id;
}

export interface AddLineOpts {
  qty?: number;
  note?: string;
  taxRate?: number;
  modifiers?: { optionId?: string | null; name: string; priceDelta: number }[];
}

/**
 * Agrega una línea (con sus modificadores) a la orden y recalcula los totales.
 * CC-7: todas las escrituras se hacen con `await` antes de devolver.
 */
export async function addOrderLineMutation(
  db: PowerSyncDrizzleDB,
  organizationId: string,
  orderId: string,
  product: { id?: string | null; name: string; unitPrice: number },
  opts: AddLineOpts = {},
): Promise<string> {
  const lineId = generateUUID();
  const now = nowISO();
  const qty = Math.max(1, Math.trunc(opts.qty ?? 1));
  const mods = opts.modifiers ?? [];

  const calcLine: CalcLine = {
    unitPrice: product.unitPrice,
    qty,
    taxRate: opts.taxRate ?? 0,
    modifiers: mods.map((m) => ({ priceDelta: m.priceDelta })),
  };
  const unitEffective = lineUnitEffectivePrice(calcLine);
  const total = lineTotal(calcLine);

  await db.insert(order_line).values({
    id: lineId,
    organization_id: organizationId,
    order_id: orderId,
    product_id: product.id ?? null,
    name: product.name,
    qty,
    unit_price: unitEffective,
    line_total: total,
    note: opts.note ?? null,
    kds_status: "pending",
    sort_order: 0,
    created_at: now,
    updated_at: now,
  });

  for (const m of mods) {
    await db.insert(order_line_modifier).values({
      id: generateUUID(),
      organization_id: organizationId,
      order_line_id: lineId,
      modifier_option_id: m.optionId ?? null,
      name: m.name,
      price_delta: Math.trunc(m.priceDelta),
      created_at: now,
      updated_at: now,
    });
  }

  await recomputeOrderTotals(db, organizationId, orderId);
  return lineId;
}

/**
 * Quita una línea y recalcula totales. ANTIFRAUDE (ACTIVITY-CORE §1b): la
 * línea NO desaparece — es un soft-delete que queda registrado en el historial
 * con actor (y motivo si es una anulación post-comanda, ver
 * `voidOrderLineMutation`). Los modificadores se conservan colgando de la
 * línea anulada para que el rastro sea completo.
 */
export async function removeOrderLineMutation(
  db: PowerSyncDrizzleDB,
  organizationId: string,
  orderId: string,
  lineId: string,
  opts?: { reason?: string },
): Promise<void> {
  const now = nowISO();
  await db
    .update(order_line)
    .set({
      deleted_at: now,
      void_reason: opts?.reason?.trim() || null,
      updated_at: now,
    })
    .where(eq(order_line.id, lineId));
  await recomputeOrderTotals(db, organizationId, orderId);
}

/**
 * Anula una línea YA COMANDADA (operación de negocio explícita, con motivo
 * opcional). Mismo mecanismo que quitarla (soft-delete registrado), pero la
 * intención queda explícita en el nombre del call-site y el motivo viaja al
 * historial ("anuló la línea X — motivo: se cayó el plato").
 */
export async function voidOrderLineMutation(
  db: PowerSyncDrizzleDB,
  organizationId: string,
  orderId: string,
  lineId: string,
  reason?: string,
): Promise<void> {
  await removeOrderLineMutation(db, organizationId, orderId, lineId, { reason });
}

/**
 * Restaura una línea anulada de una orden AÚN ABIERTA y recalcula totales
 * (inverso del void de línea; en órdenes pagadas no hay camino de vuelta).
 */
export async function restoreOrderLineMutation(
  db: PowerSyncDrizzleDB,
  organizationId: string,
  orderId: string,
  lineId: string,
): Promise<void> {
  await db
    .update(order_line)
    .set({ deleted_at: null, void_reason: null, updated_at: nowISO() })
    .where(eq(order_line.id, lineId));
  await recomputeOrderTotals(db, organizationId, orderId);
}

/**
 * Recalcula subtotal/impuesto/total de una orden a partir de sus líneas.
 * El cálculo vive en `calc.ts` (puro). Aquí solo persistimos el resultado.
 */
export async function recomputeOrderTotals(
  db: PowerSyncDrizzleDB,
  organizationId: string,
  orderId: string,
  discount?: number,
): Promise<void> {
  const lines = await db
    .select()
    .from(order_line)
    .where(and(eq(order_line.order_id, orderId), ...liveOrderLineFilters()));

  // Construimos las CalcLine. El tax_rate no se persiste por línea en F1, así que
  // recuperamos el efectivo guardado (unit_price ya incluye modificadores).
  const calcLines: CalcLine[] = lines.map((l) => ({
    unitPrice: l.unit_price ?? 0,
    qty: l.qty ?? 1,
    taxRate: 0,
  }));

  const current = await db.select().from(order).where(eq(order.id, orderId)).limit(1);
  const existingDiscount = current[0]?.discount ?? 0;
  const totals = computeOrderTotals(calcLines, discount ?? existingDiscount);

  await db
    .update(order)
    .set({
      subtotal: totals.subtotal,
      discount: totals.discount,
      tax_total: totals.taxTotal,
      total: totals.total,
      // CC-12: reenviamos branch_id en todo update de `order`.
      branch_id: current[0]?.branch_id ?? null,
      updated_at: nowISO(),
    })
    .where(and(eq(order.id, orderId), eq(order.organization_id, organizationId)));
}

export async function setOrderStatusMutation(
  db: PowerSyncDrizzleDB,
  orderId: string,
  status: "open" | "sent" | "paid" | "void",
): Promise<void> {
  // CC-12: en TODO update de `order` reenviamos `branch_id` para evitar el
  // rechazo del write-path por carrera de sync (un PATCH que omita branch_id
  // puede chocar con un upsert concurrente que aún no tiene la fila).
  const current = await db
    .select({ branch_id: order.branch_id })
    .from(order)
    .where(eq(order.id, orderId))
    .limit(1);
  const patch: Record<string, unknown> = {
    status,
    branch_id: current[0]?.branch_id ?? null,
    updated_at: nowISO(),
  };
  if (status === "paid" || status === "void") {
    patch.closed_at = nowISO();
  }
  await db.update(order).set(patch).where(eq(order.id, orderId));
}

/**
 * Anula una orden (void) con motivo opcional. La transición y el motivo quedan
 * registrados en el historial con actor (antifraude): "anuló la orden — motivo".
 */
export async function voidOrderMutation(
  db: PowerSyncDrizzleDB,
  orderId: string,
  reason?: string,
): Promise<void> {
  const current = await db
    .select({ branch_id: order.branch_id })
    .from(order)
    .where(eq(order.id, orderId))
    .limit(1);
  await db
    .update(order)
    .set({
      status: "void",
      void_reason: reason?.trim() || null,
      closed_at: nowISO(),
      branch_id: current[0]?.branch_id ?? null,
      updated_at: nowISO(),
    })
    .where(eq(order.id, orderId));
}

/**
 * Reabre una orden cerrada (paid/void → open). En un POS esto NO es un "undo":
 * es una operación de negocio explícita que queda en el historial con actor
 * ("reabrió la orden") — el cuadre del turno la lista como reapertura.
 */
export async function reopenOrderMutation(db: PowerSyncDrizzleDB, orderId: string): Promise<void> {
  const current = await db
    .select({ branch_id: order.branch_id })
    .from(order)
    .where(eq(order.id, orderId))
    .limit(1);
  await db
    .update(order)
    .set({
      status: "open",
      closed_at: null,
      void_reason: null,
      branch_id: current[0]?.branch_id ?? null,
      updated_at: nowISO(),
    })
    .where(eq(order.id, orderId));
}

/**
 * Aplica (o quita, con 0) un descuento de ticket con motivo opcional. El
 * cambio de `discount` + `discount_reason` queda registrado en el historial
 * con actor ("aplicó un descuento de X — motivo: cliente frecuente").
 */
export async function applyOrderDiscountMutation(
  db: PowerSyncDrizzleDB,
  organizationId: string,
  orderId: string,
  discount: number,
  reason?: string,
): Promise<void> {
  const value = Math.max(0, Math.trunc(discount));
  await db
    .update(order)
    .set({ discount_reason: value > 0 ? reason?.trim() || null : null, updated_at: nowISO() })
    .where(and(eq(order.id, orderId), eq(order.organization_id, organizationId)));
  await recomputeOrderTotals(db, organizationId, orderId, value);
}

/** Atribuye una orden a un empleado (mesero/cajero). CC-12: reenvía branch_id. */
export async function setOrderEmployeeMutation(
  db: PowerSyncDrizzleDB,
  orderId: string,
  employeeId: string | null,
): Promise<void> {
  const current = await db
    .select({ branch_id: order.branch_id })
    .from(order)
    .where(eq(order.id, orderId))
    .limit(1);
  await db
    .update(order)
    .set({
      employee_id: employeeId,
      branch_id: current[0]?.branch_id ?? null,
      updated_at: nowISO(),
    })
    .where(eq(order.id, orderId));
}

export async function setLineKdsStatusMutation(
  db: PowerSyncDrizzleDB,
  lineId: string,
  kdsStatus: "pending" | "cooking" | "ready" | "served",
): Promise<void> {
  await db
    .update(order_line)
    .set({ kds_status: kdsStatus, updated_at: nowISO() })
    .where(eq(order_line.id, lineId));
}

export interface PayOrderResult {
  paymentId: string;
  change: number;
}

/**
 * Registra un pago (efectivo por defecto) y, si cubre el total, marca la orden
 * como pagada. Devuelve el cambio a entregar. El cálculo de cambio es puro
 * (calc.ts). CC-7: escrituras con await.
 */
export async function payOrderCashMutation(
  db: PowerSyncDrizzleDB,
  organizationId: string,
  orderId: string,
  tendered: number,
  opts?: { method?: PaymentMethod; registerSessionId?: string | null; employeeId?: string | null },
): Promise<PayOrderResult> {
  const current = await db.select().from(order).where(eq(order.id, orderId)).limit(1);
  const total = current[0]?.total ?? 0;
  const method = opts?.method ?? "cash";
  const change = method === "cash" ? changeDue(tendered, total) : 0;
  const amount = method === "cash" ? Math.min(Math.trunc(tendered), total) : Math.trunc(tendered);

  // Atribución de venta: el empleado que cobra (para el reporte por mesero).
  const employeeId = opts?.employeeId ?? current[0]?.employee_id ?? null;

  const paymentId = generateUUID();
  const now = nowISO();
  await db.insert(payment).values({
    id: paymentId,
    organization_id: organizationId,
    order_id: orderId,
    register_session_id: opts?.registerSessionId ?? current[0]?.register_session_id ?? null,
    method,
    amount,
    tendered: Math.trunc(tendered),
    change_given: change,
    employee_id: employeeId,
    paid_at: now,
    created_at: now,
    updated_at: now,
  });

  if (employeeId && employeeId !== current[0]?.employee_id) {
    await setOrderEmployeeMutation(db, orderId, employeeId);
  }
  await setOrderStatusMutation(db, orderId, "paid");
  return { paymentId, change };
}

/**
 * Envía la orden a cocina: marca la orden como `sent` (las líneas ya nacen en
 * `kds_status = "pending"`). A partir de aquí la comanda aparece en el KDS.
 * CC-7: escritura con await.
 */
export async function sendOrderToKitchenMutation(
  db: PowerSyncDrizzleDB,
  orderId: string,
): Promise<void> {
  await setOrderStatusMutation(db, orderId, "sent");
}

/** Avanza el estado KDS de una línea pending → cooking → ready → served. */
const KDS_NEXT: Record<string, "cooking" | "ready" | "served"> = {
  pending: "cooking",
  cooking: "ready",
  ready: "served",
};

export function nextKdsStatus(
  current: string | null | undefined,
): "cooking" | "ready" | "served" | null {
  return KDS_NEXT[current ?? "pending"] ?? null;
}

/**
 * Avanza una línea al siguiente estado de cocina. No-op si ya está `served`.
 * Devuelve el nuevo estado (o null si no avanzó). Lógica de transición pura.
 */
export async function advanceLineKdsMutation(
  db: PowerSyncDrizzleDB,
  lineId: string,
  currentStatus: string | null | undefined,
): Promise<"cooking" | "ready" | "served" | null> {
  const next = nextKdsStatus(currentStatus);
  if (!next) {
    return null;
  }
  await setLineKdsStatusMutation(db, lineId, next);
  return next;
}

export interface SplitPaymentResult {
  paymentId: string;
  change: number;
  /** Saldo pendiente tras este pago (centavos). 0 = orden saldada. */
  remaining: number;
  settled: boolean;
}

/**
 * Registra UN pago parcial de una cuenta dividida, con propina opcional. Si la
 * suma de pagos (sin contar propinas) cubre el total, marca la orden `paid`.
 *
 * `amount` es el importe de la cuenta que cubre este pago (centavos). `tip` es
 * adicional. En efectivo, `tendered` permite calcular el cambio sobre
 * (amount + tip). El reparto/cuadre se calcula con helpers puros (calc.ts).
 * CC-6 (cálculo puro) + CC-7 (escrituras con await).
 */
export async function splitPaymentMutation(
  db: PowerSyncDrizzleDB,
  organizationId: string,
  orderId: string,
  amount: number,
  opts?: {
    method?: PaymentMethod;
    tip?: number;
    tendered?: number;
    registerSessionId?: string | null;
    employeeId?: string | null;
  },
): Promise<SplitPaymentResult> {
  const current = await db.select().from(order).where(eq(order.id, orderId)).limit(1);
  const total = current[0]?.total ?? 0;
  const method = opts?.method ?? "cash";
  const payAmount = Math.max(0, Math.trunc(amount));
  const tip = Math.max(0, Math.trunc(opts?.tip ?? 0));
  const employeeId = opts?.employeeId ?? current[0]?.employee_id ?? null;

  // Pagos previos de esta orden (para saber el saldo restante).
  const prior = await db
    .select()
    .from(payment)
    .where(and(eq(payment.order_id, orderId), ...livePaymentFilters()));
  const priorAmounts = prior.map((p) => p.amount ?? 0);

  // En efectivo el cliente entrega `tendered` por (importe + propina); el cambio
  // sale sobre ese conjunto. En otros métodos no hay cambio.
  const due = payAmount + tip;
  const tendered = method === "cash" ? Math.trunc(opts?.tendered ?? due) : due;
  const change = method === "cash" ? changeDue(tendered, due) : 0;

  const paymentId = generateUUID();
  const now = nowISO();
  await db.insert(payment).values({
    id: paymentId,
    organization_id: organizationId,
    order_id: orderId,
    register_session_id: opts?.registerSessionId ?? current[0]?.register_session_id ?? null,
    method,
    amount: payAmount,
    tip,
    tendered,
    change_given: change,
    employee_id: employeeId,
    paid_at: now,
    created_at: now,
    updated_at: now,
  });

  const allAmounts = [...priorAmounts, payAmount];
  const settled = isSplitSettled(total, allAmounts);
  const remaining = remainingDue(total, allAmounts);

  if (settled) {
    if (employeeId && employeeId !== current[0]?.employee_id) {
      await setOrderEmployeeMutation(db, orderId, employeeId);
    }
    await setOrderStatusMutation(db, orderId, "paid");
  }

  return { paymentId, change, remaining, settled };
}
